import { LegacyCptReport } from '../../../services/legacyCptReportService';

export const hasLegacyValue = (value: unknown): boolean => {
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }

    if (typeof value === 'boolean') {
        return true;
    }

    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return value !== null && value !== undefined;
};

export const formatLegacyDate = (value: string | null | undefined): string => {
    if (!value) return 'Not recorded';

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime())
        ? 'Not recorded'
        : parsedDate.toLocaleDateString();
};

export const formatLegacyDateTime = (
    value: string | null | undefined
): string => {
    if (!value) return 'Not recorded';

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime())
        ? 'Not recorded'
        : parsedDate.toLocaleString();
};

export const formatLegacyMinutes = (
    value: number | null | undefined
): string => {
    if (!hasLegacyValue(value)) return 'Not recorded';

    return `${value} minute${value === 1 ? '' : 's'}`;
};

export const getLegacyStatusMeta = (report: LegacyCptReport) => {
    const normalizedStatus = report.status?.toLowerCase() ?? '';

    if (report.approval.approved || normalizedStatus === 'approved') {
        return {
            label: report.status || 'Approved',
            className:
                'border-emerald-500/30 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-950/40 dark:text-emerald-200',
        };
    }

    if (normalizedStatus === 'draft' || normalizedStatus === 'pending') {
        return {
            label: report.status || 'Pending',
            className:
                'border-amber-500/30 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-200',
        };
    }

    return {
        label: report.status || 'Legacy',
        className:
            'border-border bg-background/80 text-muted-foreground',
    };
};

export const getLegacyCptCodeLabels = (
    report: LegacyCptReport
): string[] => {
    const codes = [...report.summary.codes];

    if (!codes.length) {
        if (report.summary.cpt76014) {
            codes.push('76014');
        }

        if ((report.summary.count15 ?? report.summary.cpt76015 ?? 0) > 0) {
            codes.push('76015');
        }
    }

    return Array.from(
        new Set(
            codes
                .map((code) => code.replace(/^CPT\s*/i, '').trim())
                .filter(Boolean)
        )
    );
};

export const getLegacyReportSummary = (report: LegacyCptReport): string => {
    const segments = [
        report.mriSafetyDecisionInfo.implantDetails.model,
        report.info.purpose,
        report.guidance.recommendation,
        report.summary.notes,
        report.info.notes,
    ].filter((segment): segment is string => !!segment?.trim());

    return segments.length
        ? segments.join(' • ')
        : 'Legacy CPT report available for preview only.';
};

export const getLegacyServiceDateLabel = (report: LegacyCptReport): string =>
    formatLegacyDate(report.info.dateOfService || report.info.dateOfAssessment);

export const getLegacyUpdatedAtLabel = (report: LegacyCptReport): string =>
    formatLegacyDateTime(report.updatedAt);
